import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { format, addDays } from "date-fns";
import { listMealPlans, searchMealPlanPreferences } from "../api/OtterApi";
import { useNotificationContext } from "../contexts/NotificationContext";
import {
    MONTH_NAMES,
    DAYS_OF_THE_WEEK,
    MAX_TILES_PER_CALENDAR_MONTH,
    DAYS_PER_WEEK,
} from "../pages/general/Constant";
import "./WeekSelectionCalendar.css";

const getDaysOfTheMonth = (scopeDate) => {
    const days = [];
    var week = 0;
    var date = new Date(scopeDate.getFullYear(), scopeDate.getMonth(), 1);
    date.setDate(date.getDate() - date.getDay());

    for (var i = 0; i < MAX_TILES_PER_CALENDAR_MONTH; i++) {
        if (week === days.length) {
            days.push([]);
        }
        days[week].push(
            date.getMonth() === scopeDate.getMonth()
                ? new Date(date)
                : new Date(date),
        );
        date.setDate(date.getDate() + 1);
        if (days[week].length >= DAYS_PER_WEEK) {
            week += 1;
            if (date.getMonth() !== scopeDate.getMonth()) {
                break;
            }
        }
    }

    return days;
};

const WeekSelectionCalendar = ({ onWeekHover }) => {
    const { getAccessTokenSilently } = useAuth0();
    const history = useHistory();
    const { addNotification } = useNotificationContext();
    const today = new Date();

    const [scopeDate, setScopeDate] = useState(
        new Date(today.getFullYear(), today.getMonth(), 1),
    );
    const [tileData, setTileData] = useState(getDaysOfTheMonth(scopeDate));
    const [hoveredWeekIndex, setHoveredWeekIndex] = useState(null);
    const [hoveredDayIndex, setHoveredDayIndex] = useState(null);
    const [mealPlansByDate, setMealPlansByDate] = useState({});
    const monthTitle = `${
        MONTH_NAMES[scopeDate.getMonth()]
    } ${scopeDate.getFullYear()}`;

    useEffect(() => {
        setTileData(getDaysOfTheMonth(scopeDate));
    }, [scopeDate]);

    // Fetch existing meal plans for the current month
    useEffect(() => {
        const fetchMealPlans = async () => {
            try {
                const token = await getAccessTokenSilently();
                // Fetch all meal plans
                const response = await listMealPlans(token, 100, 0, "");

                if (response.status === 200 && response.data.mealplans) {
                    // Create a map of date -> meal count for that date
                    const dateMap = {};

                    // Helper to convert day name to date within a week range
                    const getDayOfWeekDate = (startDate, dayName) => {
                        const dayMap = {
                            Sunday: 0,
                            Monday: 1,
                            Tuesday: 2,
                            Wednesday: 3,
                            Thursday: 4,
                            Friday: 5,
                            Saturday: 6,
                        };
                        const targetDay = dayMap[dayName];
                        if (targetDay === undefined) return null;

                        const start = new Date(startDate);
                        const startDay = start.getDay();
                        const daysToAdd = targetDay - startDay;
                        const resultDate = new Date(start);
                        resultDate.setDate(start.getDate() + daysToAdd);
                        return resultDate;
                    };

                    // For each meal plan, fetch its preferences to get planned meals
                    const fetchPromises = response.data.mealplans.map(
                        async (mp) => {
                            try {
                                // Parse the meal plan name to get the start date
                                // Format: "2025/11/10 - 2025/11/16"
                                const nameParts = mp.name.split(" - ");
                                if (nameParts.length !== 2) return;

                                const startDateStr = nameParts[0].replace(
                                    /\//g,
                                    "-",
                                );

                                const prefsResponse =
                                    await searchMealPlanPreferences(
                                        token,
                                        100,
                                        0,
                                        "",
                                        mp.id,
                                    );
                                if (
                                    prefsResponse.status === 200 &&
                                    prefsResponse.data.mealplanpreferences
                                ) {
                                    prefsResponse.data.mealplanpreferences.forEach(
                                        (pref) => {
                                            if (
                                                pref.plannedmeal_preferences &&
                                                Array.isArray(
                                                    pref.plannedmeal_preferences,
                                                )
                                            ) {
                                                pref.plannedmeal_preferences.forEach(
                                                    (pm) => {
                                                        if (pm.day) {
                                                            const mealDate =
                                                                getDayOfWeekDate(
                                                                    startDateStr,
                                                                    pm.day,
                                                                );
                                                            if (mealDate) {
                                                                const dateKey =
                                                                    format(
                                                                        mealDate,
                                                                        "yyyy-MM-dd",
                                                                    );
                                                                if (
                                                                    !dateMap[
                                                                        dateKey
                                                                    ]
                                                                ) {
                                                                    dateMap[
                                                                        dateKey
                                                                    ] = 0;
                                                                }
                                                                dateMap[
                                                                    dateKey
                                                                ]++;
                                                            }
                                                        }
                                                    },
                                                );
                                            }
                                        },
                                    );
                                }
                            } catch (err) {
                                console.error(
                                    "Error fetching meal plan preferences:",
                                    err,
                                );
                            }
                        },
                    );

                    await Promise.all(fetchPromises);
                    console.log("Meal plans by date:", dateMap);
                    setMealPlansByDate(dateMap);
                }
            } catch (error) {
                console.error("Error fetching meal plans:", error);
            }
        };

        fetchMealPlans();
    }, [getAccessTokenSilently, scopeDate]);

    useEffect(() => {
        // Notify parent component when hover changes
        if (
            onWeekHover &&
            hoveredWeekIndex !== null &&
            hoveredDayIndex !== null
        ) {
            const weekName = getMealPlanNameForWeek(
                hoveredWeekIndex,
                hoveredDayIndex,
            );
            onWeekHover(weekName);
        } else if (onWeekHover) {
            onWeekHover(null);
        }
    }, [hoveredWeekIndex, hoveredDayIndex, onWeekHover, tileData]);

    const updateMonth = (delta) => {
        const newScopeDate = new Date(scopeDate);
        newScopeDate.setMonth(scopeDate.getMonth() + delta);
        setScopeDate(newScopeDate);
    };

    const jumpToToday = () => {
        if (!isSameYearMonth(scopeDate)) {
            const homeDate = new Date(today);
            homeDate.setDate(1);
            setScopeDate(homeDate);
        }
    };

    const isSameYearMonth = (date) => {
        return (
            date.getFullYear() === today.getFullYear() &&
            date.getMonth() === today.getMonth()
        );
    };

    const getScopeLabelClassName = (scopeDate) => {
        let suffix = "";

        if (
            scopeDate.getFullYear() === today.getFullYear() &&
            scopeDate.getMonth() === today.getMonth()
        ) {
            suffix = "-home";
        }

        return `scope-label${suffix}`;
    };

    const craftDayColumnClassName = (day, weekIndex, dayIndex) => {
        let suffix = "";

        if (day) {
            // Check if this day should be highlighted (Monday to Sunday range)
            let isHighlighted = false;
            if (hoveredWeekIndex !== null && hoveredDayIndex !== null) {
                // If hovering on Sunday (dayIndex 0), highlight it and the previous 6 days
                if (hoveredDayIndex === 0) {
                    if (weekIndex === hoveredWeekIndex && dayIndex === 0) {
                        isHighlighted = true;
                    } else if (
                        weekIndex === hoveredWeekIndex - 1 &&
                        dayIndex >= 1
                    ) {
                        isHighlighted = true;
                    }
                } else {
                    // For Monday-Saturday (dayIndex 1-6), highlight from Monday through the following Sunday
                    if (weekIndex === hoveredWeekIndex && dayIndex >= 1) {
                        isHighlighted = true;
                    } else if (
                        weekIndex === hoveredWeekIndex + 1 &&
                        dayIndex === 0
                    ) {
                        isHighlighted = true;
                    }
                }
            }

            if (
                // Previous year
                day.getFullYear() < today.getFullYear() ||
                // Previous month of current year
                (day.getFullYear() === today.getFullYear() &&
                    day.getMonth() < today.getMonth()) ||
                // Day is this month, but in the past
                (day.getFullYear() === today.getFullYear() &&
                    day.getMonth() === today.getMonth() &&
                    day.getDate() < today.getDate())
            ) {
                suffix = "-past";
            }

            if (isHighlighted) {
                suffix += "-highlighted";
            }
        }

        return `day-column${suffix}`;
    };

    const craftDateBoxClassName = (day) => {
        let suffix = "";
        if (day) {
            if (
                day.getFullYear() !== scopeDate.getFullYear() ||
                day.getMonth() !== scopeDate.getMonth()
            ) {
                suffix = "-other-month";
            } else if (
                day.getFullYear() === today.getFullYear() &&
                day.getMonth() === today.getMonth() &&
                day.getDate() === today.getDate()
            ) {
                suffix = "-today";
            }
        }

        return `date-box${suffix}`;
    };

    const getMealPlanNameForWeek = (weekIndex, dayIndex) => {
        // Monday-Sunday week
        let monday, sunday;

        if (dayIndex === 0) {
            // Clicked on Sunday, so Monday is in the previous week
            if (weekIndex > 0) {
                monday = tileData[weekIndex - 1][1]; // Monday of previous row
                sunday = tileData[weekIndex][0]; // Sunday of current row
            } else {
                return null;
            }
        } else {
            // Clicked on Monday-Saturday, Sunday is in the next week
            monday = tileData[weekIndex][1]; // Monday of current row
            if (weekIndex < tileData.length - 1) {
                sunday = tileData[weekIndex + 1][0]; // Sunday of next row
            } else {
                // Handle case where there's no next week
                const lastDay = tileData[weekIndex][6];
                sunday = addDays(lastDay, 1); // Add 1 day to Saturday
            }
        }

        const dateFormat = "yyyy/MM/dd";
        const formattedStartDate = format(monday, dateFormat);
        const formattedEndDate = format(sunday, dateFormat);

        return `${formattedStartDate} - ${formattedEndDate}`;
    };

    const getMealCountForDate = (day) => {
        if (!day) return 0;
        const dateKey = format(day, "yyyy-MM-dd");
        const count = mealPlansByDate[dateKey] || 0;
        return count;
    };

    const handleWeekClick = async (weekIndex, dayIndex) => {
        const mealPlanName = getMealPlanNameForWeek(weekIndex, dayIndex);

        if (!mealPlanName) {
            return;
        }

        try {
            const token = await getAccessTokenSilently();
            // Search for meal plan with this name
            const response = await listMealPlans(token, 100, 0, mealPlanName);

            if (response.status === 200 && response.data.mealplans.length > 0) {
                const exactMatches = response.data.mealplans.filter(
                    (mp) => mp.name === mealPlanName,
                );
                if (exactMatches.length > 1) {
                    addNotification({
                        message: `⚠️ Multiple meal plans with name ${mealPlanName} found, getting the first one.`,
                    });
                }
                const matchingMealPlan = exactMatches[0];
                if (matchingMealPlan) {
                    history.push(`/mealplan/${matchingMealPlan.id}`);
                } else {
                    history.push(
                        `/mealplan/create?name=${encodeURIComponent(mealPlanName)}`,
                    );
                }
            } else {
                // No meal plans found, redirect to create page with the name
                history.push(
                    `/mealplan/create?name=${encodeURIComponent(mealPlanName)}`,
                );
            }
        } catch (error) {
            console.error("Error fetching meal plan:", error);
            // On error, still redirect to create page
            history.push(
                `/mealplan/create?name=${encodeURIComponent(mealPlanName)}`,
            );
        }
    };

    return (
        <div className="week-calendar-widget">
            <div className="month-row">
                <div className="month-arrow" onClick={() => updateMonth(-1)}>
                    {"< Previous"}
                </div>
                <div className="month-title">
                    <h2>{monthTitle}</h2>
                    <div
                        className={getScopeLabelClassName(scopeDate)}
                        onClick={() => {
                            jumpToToday();
                        }}
                    >
                        {isSameYearMonth(scopeDate)
                            ? "This month"
                            : "Jump to today"}
                    </div>
                </div>
                <div className="month-arrow" onClick={() => updateMonth(1)}>
                    {"Next >"}
                </div>
            </div>
            <div className="day-name-row">
                {DAYS_OF_THE_WEEK.map((day, index) => {
                    return (
                        <div key={`day-${index}`} className="day-of-the-week">
                            {day}
                        </div>
                    );
                })}
            </div>
            {tileData.map((week, weekIndex) => {
                return (
                    <div key={weekIndex} className="week-row">
                        {week.map((day, dayIndex) => {
                            const mealCount = getMealCountForDate(day);
                            return (
                                <div
                                    key={dayIndex}
                                    className={craftDayColumnClassName(
                                        day,
                                        weekIndex,
                                        dayIndex,
                                    )}
                                    onMouseEnter={() => {
                                        setHoveredWeekIndex(weekIndex);
                                        setHoveredDayIndex(dayIndex);
                                    }}
                                    onMouseLeave={() => {
                                        setHoveredWeekIndex(null);
                                        setHoveredDayIndex(null);
                                    }}
                                    onClick={() =>
                                        handleWeekClick(weekIndex, dayIndex)
                                    }
                                >
                                    <div className={craftDateBoxClassName(day)}>
                                        {day ? day.getDate() : null}
                                    </div>
                                    {mealCount > 0 && (
                                        <div
                                            className="meal-count-indicator"
                                            title={`${mealCount} meal${
                                                mealCount > 1 ? "s" : ""
                                            } planned`}
                                        >
                                            {mealCount} meal
                                            {mealCount > 1 ? "s" : ""}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                );
            })}
        </div>
    );
};

export default WeekSelectionCalendar;
